import * as THREE from 'three';
import type { InputManager } from '../../input';
import type { Astronaut } from './Astronaut';
import type { Terrain } from '../surface/Terrain';
import type { SurfaceObstacle } from '../surface/SurfaceObstacle';
import type { AudioManager } from '../../audio';
import type { PlayerStats } from '../player';

export interface FirstPersonConfig {
  readonly walkSpeed: number;
  readonly runMultiplier: number;
  readonly acceleration: number;
  readonly jumpVelocity: number;
  readonly gravity: number;
  readonly airControl: number;
  readonly eyeHeight: number;
  readonly mouseSensitivity: number;
  readonly minPitch: number;
  readonly maxPitch: number;
  readonly headBobAmplitude: number;
  readonly headBobRate: number;
  /** Soft circular boundary radius. 0 disables it. Used on planet surfaces
   *  to keep the astronaut inside the visible terrain without showing the
   *  geometry edge. Has no effect on station interiors (radius left at 0). */
  readonly boundaryRadius: number;
  /** Closing speed (u/s) above which a horizontal collision deals health
   *  damage. Walking into a rock is below this; running into one isn't. */
  readonly collisionDamageThreshold: number;
  readonly collisionDamageScale: number;
  readonly collisionDamageCap: number;
  /** Vertical impact speed (positive number, u/s) above which a fall
   *  deals damage. Tuned so a normal jump landing is fine but a leap from
   *  a tall ridge stings. */
  readonly fallDamageThreshold: number;
  readonly fallDamageScale: number;
  readonly fallDamageCap: number;
}

export const DEFAULT_FPS_CONFIG: FirstPersonConfig = {
  walkSpeed: 5.5,
  runMultiplier: 1.75,
  acceleration: 32,
  jumpVelocity: 7.4,
  gravity: 18,
  airControl: 0.55,
  eyeHeight: 1.62,
  mouseSensitivity: 0.0023,
  minPitch: -Math.PI * 0.48,
  maxPitch: Math.PI * 0.48,
  headBobAmplitude: 0.05,
  headBobRate: 9.5,
  boundaryRadius: 0,
  collisionDamageThreshold: 7,
  collisionDamageScale: 2.4,
  collisionDamageCap: 26,
  fallDamageThreshold: 12,
  fallDamageScale: 2.6,
  fallDamageCap: 55
};

export class FirstPersonController {
  private readonly moveDir = new THREE.Vector3();
  private readonly forward = new THREE.Vector3();
  private readonly right = new THREE.Vector3();
  private readonly next = new THREE.Vector3();
  private readonly cameraEuler = new THREE.Euler(0, 0, 0, 'YXZ');
  private yaw = 0;
  private pitch = 0;
  private enabled = false;
  private bobPhase = 0;
  private stepTimer = 0;
  private wasGrounded = true;

  // Per-obstacle damage cooldown so a freshly-bumped rock can't drain
  // health frame-after-frame while the astronaut is still grinding against
  // it post-resolve.
  private readonly hitCooldowns = new Map<SurfaceObstacle, number>();

  constructor(
    private readonly astronaut: Astronaut,
    private readonly input: InputManager,
    private readonly camera: THREE.PerspectiveCamera,
    private readonly terrain: Terrain,
    private readonly obstacles: readonly SurfaceObstacle[],
    private readonly config: FirstPersonConfig = DEFAULT_FPS_CONFIG,
    private readonly audio: AudioManager | null = null,
    private readonly stats: PlayerStats | null = null
  ) {}

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.astronaut.velocity.x = 0;
      this.astronaut.velocity.z = 0;
    }
  }

  setYaw(yaw: number): void {
    this.yaw = wrapAngle(yaw);
  }

  setPitch(pitch: number): void {
    this.pitch = THREE.MathUtils.clamp(
      pitch,
      this.config.minPitch,
      this.config.maxPitch
    );
  }

  getYaw(): number {
    return this.yaw;
  }

  syncCamera(): void {
    const pos = this.astronaut.object3D.position;
    const hSpeed = Math.sqrt(
      this.astronaut.velocity.x * this.astronaut.velocity.x +
        this.astronaut.velocity.z * this.astronaut.velocity.z
    );

    let bobY = 0;
    let bobX = 0;
    if (this.astronaut.isGrounded && hSpeed > 0.4) {
      const speedFactor = Math.min(hSpeed / this.config.walkSpeed, 1.6);
      const amp = this.config.headBobAmplitude * speedFactor;
      bobY = Math.abs(Math.sin(this.bobPhase)) * amp;
      bobX = Math.sin(this.bobPhase * 0.5) * amp * 0.35;
    }

    this.camera.position.set(
      pos.x + bobX,
      pos.y + this.config.eyeHeight + bobY,
      pos.z
    );

    this.cameraEuler.set(this.pitch, this.yaw, 0, 'YXZ');
    this.camera.quaternion.setFromEuler(this.cameraEuler);
  }

  update(dt: number): void {
    if (this.enabled && this.input.isPointerLocked()) {
      const dx = this.input.getPointerDeltaX();
      const dy = this.input.getPointerDeltaY();
      this.yaw = wrapAngle(this.yaw - dx * this.config.mouseSensitivity);
      this.pitch = THREE.MathUtils.clamp(
        this.pitch - dy * this.config.mouseSensitivity,
        this.config.minPitch,
        this.config.maxPitch
      );
    }

    let forwardInput = 0;
    let rightInput = 0;
    let running = false;
    let jump = false;

    if (this.enabled) {
      forwardInput =
        (this.input.isKeyDown('KeyW') ? 1 : 0) -
        (this.input.isKeyDown('KeyS') ? 1 : 0);
      rightInput =
        (this.input.isKeyDown('KeyD') ? 1 : 0) -
        (this.input.isKeyDown('KeyA') ? 1 : 0);
      running = this.input.isKeyDown('ShiftLeft');
      jump = this.input.isKeyJustPressed('Space');
    }

    this.forward.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    this.right.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw));

    this.moveDir
      .set(0, 0, 0)
      .addScaledVector(this.forward, forwardInput)
      .addScaledVector(this.right, rightInput);

    const hasInput = this.moveDir.lengthSq() > 1e-4;
    if (hasInput) this.moveDir.normalize();

    const astronaut = this.astronaut;
    const velocity = astronaut.velocity;
    const pos = astronaut.object3D.position;

    const targetSpeed =
      this.config.walkSpeed * (running ? this.config.runMultiplier : 1);
    const controlFactor = astronaut.isGrounded ? 1 : this.config.airControl;
    const accel = this.config.acceleration * controlFactor;

    if (hasInput) {
      const desiredX = this.moveDir.x * targetSpeed;
      const desiredZ = this.moveDir.z * targetSpeed;
      const dx = desiredX - velocity.x;
      const dz = desiredZ - velocity.z;
      const dMag = Math.sqrt(dx * dx + dz * dz);
      if (dMag > 0) {
        const step = Math.min(accel * dt, dMag);
        velocity.x += (dx / dMag) * step;
        velocity.z += (dz / dMag) * step;
      }
    } else if (astronaut.isGrounded) {
      const decay = Math.pow(0.00025, dt);
      velocity.x *= decay;
      velocity.z *= decay;
      if (Math.abs(velocity.x) < 0.02) velocity.x = 0;
      if (Math.abs(velocity.z) < 0.02) velocity.z = 0;
    }

    if (jump && astronaut.isGrounded) {
      velocity.y = this.config.jumpVelocity;
      astronaut.isGrounded = false;
      this.audio?.playJump();
    }

    velocity.y -= this.config.gravity * dt;

    this.next.copy(pos).addScaledVector(velocity, dt);
    this.resolveObstacleCollisions(this.next, astronaut.collisionRadius, dt);
    this.enforceBoundary(this.next);

    // Use the raw terrain height so the player can descend into water
    // valleys and trigger the underwater experience. The ship still uses
    // `getCollisionGroundY` so it never sits below the lake surface.
    const groundY = this.terrain.getHeightAt(this.next.x, this.next.z);
    const penetration = groundY - this.next.y;
    const fallSpeed = velocity.y;
    if (penetration >= 0) {
      this.next.y = groundY;
      if (velocity.y < 0) velocity.y = 0;
      astronaut.isGrounded = true;
    } else if (-penetration < 0.32 && velocity.y <= 0.1) {
      this.next.y = groundY;
      velocity.y = 0;
      astronaut.isGrounded = true;
    } else {
      astronaut.isGrounded = false;
    }

    if (!this.wasGrounded && astronaut.isGrounded) {
      if (fallSpeed < -2) this.audio?.playLand();
      const impact = -fallSpeed; // positive number
      if (
        this.stats &&
        impact > this.config.fallDamageThreshold
      ) {
        const damage = Math.min(
          this.config.fallDamageCap,
          (impact - this.config.fallDamageThreshold) *
            this.config.fallDamageScale
        );
        if (damage > 0) this.stats.damageHealth(damage);
      }
    }
    this.wasGrounded = astronaut.isGrounded;

    pos.copy(this.next);
    astronaut.object3D.rotation.y = this.yaw + Math.PI;

    const hSpeed = Math.sqrt(
      velocity.x * velocity.x + velocity.z * velocity.z
    );
    if (astronaut.isGrounded && hSpeed > 0.4) {
      this.bobPhase += dt * this.config.headBobRate * (hSpeed / this.config.walkSpeed);

      this.stepTimer += dt;
      const isRunPace =
        running && hSpeed > this.config.walkSpeed * 1.05;
      const stepInterval = isRunPace ? 0.3 : 0.46;
      if (this.stepTimer >= stepInterval) {
        this.stepTimer = 0;
        this.audio?.playFootstep(isRunPace);
      }
    } else {
      this.stepTimer = 0;
    }

    astronaut.tick(dt, {
      horizontalSpeed: hSpeed,
      isGrounded: astronaut.isGrounded,
      isMoving: hasInput,
      isRunning: running && hSpeed > this.config.walkSpeed * 1.1,
      verticalSpeed: velocity.y
    });

    this.syncCamera();
  }

  private resolveObstacleCollisions(
    candidate: THREE.Vector3,
    radius: number,
    dt: number
  ): void {
    const velocity = this.astronaut.velocity;
    // Approximate astronaut vertical bounds — feet at candidate.y, head a
    // little above eye height.
    const headY = candidate.y + this.config.eyeHeight + 0.2;
    const footY = candidate.y - 0.1;

    for (const [obstacle, cooldown] of this.hitCooldowns) {
      const next = cooldown - dt;
      if (next <= 0) this.hitCooldowns.delete(obstacle);
      else this.hitCooldowns.set(obstacle, next);
    }

    for (const obstacle of this.obstacles) {
      // Vertical overlap check — flying creatures (gliders, spectres)
      // sit well above the ground so their obstacle should not block
      // foot-traffic underneath. Falls back to a height of 5u so any
      // legacy obstacle without a height still behaves like a tall rock.
      const obstacleBottom = obstacle.position.y;
      const obstacleTop = obstacle.position.y + (obstacle.height ?? 5);
      if (obstacleBottom > headY) continue;
      if (obstacleTop < footY) continue;

      const dx = candidate.x - obstacle.position.x;
      const dz = candidate.z - obstacle.position.z;
      const sum = radius + obstacle.radius;
      const distSq = dx * dx + dz * dz;
      if (distSq >= sum * sum) continue;
      const dist = Math.sqrt(Math.max(distSq, 1e-6));
      const push = sum - dist;
      const nx = dx / dist;
      const nz = dz / dist;
      candidate.x += nx * push;
      candidate.z += nz * push;

      // Closing speed along the contact normal — gentle leans against a
      // rock are below the threshold, sprinting into one isn't. `n`
      // points outward from the obstacle, so v·n is negative when the
      // astronaut is moving into it; closing is the positive magnitude
      // of that inward component.
      const closing = -(velocity.x * nx + velocity.z * nz);
      if (closing > 0) {
        // Cancel the inward radial component so the astronaut just stops
        // at the obstacle surface instead of bouncing off it. Adding
        // closing*n along the outward normal exactly negates the inward
        // velocity component we just measured.
        velocity.x += closing * nx;
        velocity.z += closing * nz;

        if (
          this.stats &&
          closing > this.config.collisionDamageThreshold &&
          !this.hitCooldowns.has(obstacle)
        ) {
          const damage = Math.min(
            this.config.collisionDamageCap,
            (closing - this.config.collisionDamageThreshold) *
              this.config.collisionDamageScale
          );
          if (damage > 0) {
            this.stats.damageHealth(damage);
            this.hitCooldowns.set(obstacle, ASTRONAUT_HIT_REARM_SECONDS);
          }
        }
      }
    }
  }

  /** Soft circular boundary — clamp inside, kill radial velocity, snap
   *  yaw to face origin. Same UX as the surface ship: the astronaut just
   *  "turns around" at the edge of the explorable zone. */
  private enforceBoundary(candidate: THREE.Vector3): void {
    const r = this.config.boundaryRadius;
    if (r <= 0) return;
    const distSq = candidate.x * candidate.x + candidate.z * candidate.z;
    if (distSq <= r * r) return;

    const dist = Math.sqrt(distSq);
    const nx = candidate.x / dist;
    const nz = candidate.z / dist;
    candidate.x = nx * (r - 0.5);
    candidate.z = nz * (r - 0.5);

    const velocity = this.astronaut.velocity;
    const radialV = velocity.x * nx + velocity.z * nz;
    if (radialV > 0) {
      velocity.x -= radialV * nx;
      velocity.z -= radialV * nz;
    }

    // forward = (-sin(yaw), -cos(yaw)) → face origin → yaw = atan2(nx, nz).
    this.yaw = Math.atan2(nx, nz);
  }
}

const ASTRONAUT_HIT_REARM_SECONDS = 0.5;

function wrapAngle(angle: number): number {
  let a = angle;
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}
